/* global bankReconMatchOf, BANK_RECON_MATCH, bankReconMatchSummary, runtime, mergeState, getReportsModel, safeArray, num, getBankLineStatus, getBankAccountLabel, getLedgerAccountCode, buildLedgerLines, ledgerLineAgeBucket, t */
// BANKAVSTEMMING — lesemodell for byråets bankskjerm
//
// Bankskjermen i avstemmingsmappa trenger noe den generiske kontoflaten ikke
// har: hvilke bankkontoer foretaket har, om bankgrunnlaget dekker hele perioden,
// og hva som står igjen linje for linje. Alt det finnes allerede i appen —
// `settings.bankAccounts` og `state.bankStatements` — men ikke samlet for én
// måned.
//
// Modellen er READ-ONLY. Den regner ikke ut bokført saldo selv: den kommer fra
// samme saldobalanse som resten av avstemmingen (`getReportsModel`). Regnet
// bankskjermen ut sitt eget tall, kunne en bankkonto vært «avstemt» mot noe
// rapportene ikke kjente igjen.
//
// Den finner heller ikke opp varsler. Hvert flagg peker på noe dataene faktisk
// sier — at en linje ikke er bokført, at et bilag mangler, at to linjer er like.
// Designets `bankgebyr`/`rente`-flagg er IKKE portert som tekstsøk: de ville
// krevd en nøkkelordsregel på fritekst uten kilde. Gebyrer og renter kommer i
// stedet fra hovedboken, der de faktisk er ført på en kostnadskonto.

const bankReconMonthRange = (periodId) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return null;
  const pad = (value) => String(value).padStart(2, '0');
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return {
    year,
    monthIndex,
    from: `${year}-${pad(monthIndex + 1)}-01`,
    to: `${year}-${pad(monthIndex + 1)}-${pad(lastDay)}`,
    days: lastDay,
  };
};

// Linjer fra alle utskrifter for én bankkonto, avgrenset til måneden. Utskrifter
// overlapper ofte i kantene, så avgrensningen skjer på linjedatoen — ikke på
// utskriftens egen periode.
const bankReconLinesFor = (state, account, range) => {
  const rows = [];
  for (const statement of safeArray(state.bankStatements)) {
    if (String(statement?.bankAccountId || '') !== String(account.id)) continue;
    const source = statement.source || 'business';
    for (const line of safeArray(statement.lines)) {
      if (line?.reconciliationStatus === 'import-removed' || line?.importCorrectionAction === 'removed') continue;
      const date = String(line?.date || '');
      if (!date || date < range.from || date > range.to) continue;
      rows.push({ line, statement, source, date });
    }
  }
  return rows.sort((a, b) => a.date.localeCompare(b.date));
};

const bankReconFlags = (entry, duplicateKeys) => {
  const flags = [];
  const line = entry.line;
  if (line.reconciliationStatus === 'import-removed' || line.importCorrectionAction === 'removed') return flags;
  if (!line.matchedEntryId && !line.bookingType && !line.invoiceId && !line.accountCode) flags.push('ikke_bokfort');
  if (line.reconciliationStatus === 'manual-review') flags.push('til_kontroll');
  // Et bilag mangler bare der appen selv krever et: en utbetaling uten vedlegg,
  // uten fritak og uten at bankutskriften er godkjent som dokumentasjon.
  if (num(line.amount) < 0 && !line.attachmentName && !line.receiptWaived
    && !['bank-statement', 'waived'].includes(line.documentationMode)) flags.push('mangler_bilag');
  if (duplicateKeys.get(bankReconDuplicateKey(entry)) > 1) flags.push('mulig_duplikat');
  return flags;
};

// To linjer med samme konto, dato, beløp og tekst er et duplikat verdt å se på.
// Det er et forslag til gjennomgang, ikke en konklusjon: en leverandør kan bli
// betalt to like beløp samme dag.
const bankReconDuplicateKey = (entry) => [
  entry.line.bankAccountId || '',
  entry.date,
  num(entry.line.amount).toFixed(2),
  String(entry.line.description || '').trim().toLowerCase(),
].join('|');


// ------------------------------------------------------------------
// Det hovedboken vet og bankutskriften ikke
// ------------------------------------------------------------------
// En bokføring på bankkontoen uten en tilsvarende banklinje er enten en
// betaling som ennå ikke har nådd banken (transitt), eller en feil. Hvilken av
// delene avgjøres ikke her — men den skal ikke kunne stå ubemerket, og en
// saldosammenlikning alene fanger den ikke: den vises som differanse uten å si
// hva differansen består av.
//
// Matchingen er bevisst enkel: samme beløp innen et vindu på dager. Den er et
// FORSLAG om at linjene hører sammen, ikke en bokføringsbeslutning.
const BANK_RECON_MATCH_WINDOW_DAYS = 6;

const bankReconDaysBetween = (left, right) => {
  const a = new Date(String(left) + 'T00:00:00');
  const b = new Date(String(right) + 'T00:00:00');
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return Infinity;
  return Math.abs(Math.round((a - b) / 86400000));
};

const bankReconUnmatchedPostings = ({ ledgerLines, bankLines, referenceDate }) => {
  const used = new Set();
  const open = [];
  const pairs = new Map();
  for (const posting of ledgerLines) {
    const hit = bankLines.findIndex((line, index) => !used.has(index)
      && Math.abs(num(line.amount) - num(posting.amount)) <= 0.005
      && bankReconDaysBetween(line.date, posting.date) <= BANK_RECON_MATCH_WINDOW_DAYS);
    // Treffet er ogsaa svaret paa den andre halvdelen av sporsmaalet: hvilken
    // bokforing banklinjen hoerer til. Samme regel maa gi begge sidene, ellers
    // kan en linje vaere «matchet» i tabellen og «uten banktreff» i transitt.
    if (hit >= 0) {
      used.add(hit);
      pairs.set(bankLines[hit].id, posting);
      continue;
    }
    open.push({
      ...posting,
      ageBucket: ledgerLineAgeBucket(posting.date, referenceDate),
      // Alderen i dager, ikke bare i boette: 30-dagersgrensen er en faglig
      // grense, og da maa flata kunne vise hvor naer en post ligger den.
      ageDays: bankReconDaysBetween(posting.date, referenceDate),
      // En postering som er eldre enn matchevinduet er ikke lenger «i transitt»
      // i noen rimelig forstand. Den er noe som må undersøkes.
      stale: bankReconDaysBetween(posting.date, referenceDate) > 30,
    });
  }
  return { open, pairs };
};

// Interne overføringer mellom foretakets egne bankkontoer. Et bilag som rører to
// av foretakets bankkontoer ER en intern overføring — det trenger ingen
// gjetning på tekst.
const bankReconInternalTransfers = (ledgerLines, ledgerCodes) => {
  const byVoucher = new Map();
  for (const line of ledgerLines) {
    if (!ledgerCodes.has(line.accountCode)) continue;
    if (!byVoucher.has(line.voucherId)) byVoucher.set(line.voucherId, []);
    byVoucher.get(line.voucherId).push(line);
  }
  const transfers = [];
  for (const [voucherId, lines] of byVoucher) {
    const accounts = new Set(lines.map((line) => line.accountCode));
    if (accounts.size < 2) continue;
    const out = lines.filter((line) => line.amount < 0).reduce((sum, line) => sum + line.amount, 0);
    const into = lines.filter((line) => line.amount > 0).reduce((sum, line) => sum + line.amount, 0);
    transfers.push({
      voucherId,
      date: lines[0].date,
      name: lines[0].voucherName || lines[0].description,
      from: lines.filter((line) => line.amount < 0).map((line) => line.accountCode).join(', '),
      to: lines.filter((line) => line.amount > 0).map((line) => line.accountCode).join(', '),
      out: Math.round(out * 100) / 100,
      into: Math.round(into * 100) / 100,
      difference: Math.round((into + out) * 100) / 100,
    });
  }
  return transfers.sort((a, b) => a.date.localeCompare(b.date));
};

// Gebyrer og renter, lest av hovedboken og ikke av teksten på banklinjen: et
// bilag som rører bankkontoen og en kostnads- eller finanskonto ER et gebyr
// eller en rente ført dit. Konteringen er kilden, ikke ordvalget.
const bankReconFeesAndInterest = (ledgerLines, ledgerCodes) => {
  const byVoucher = new Map();
  for (const line of ledgerLines) {
    if (!byVoucher.has(line.voucherId)) byVoucher.set(line.voucherId, []);
    byVoucher.get(line.voucherId).push(line);
  }
  const rows = [];
  for (const [voucherId, lines] of byVoucher) {
    if (!lines.some((line) => ledgerCodes.has(line.accountCode))) continue;
    for (const line of lines) {
      if (ledgerCodes.has(line.accountCode)) continue;
      const code = String(line.accountCode);
      // 7770 gebyrer, 8050/8150 renteinntekt og rentekostnad. Kontoklassene er
      // norske standardkontoer; er kontoplanen en annen, faller posten ut av
      // seksjonen i stedet for aa bli feilklassifisert.
      const kind = code.startsWith('777') ? 'Gebyr'
        : code.startsWith('805') ? 'Renteinntekt'
          : code.startsWith('815') ? 'Rentekostnad'
            : null;
      if (!kind) continue;
      rows.push({ voucherId, date: line.date, kind, accountCode: code, description: line.description, amount: line.amount });
    }
  }
  return rows.sort((a, b) => a.date.localeCompare(b.date));
};

// Getters: oversatt ved lesing (render), ikke ved modullasting. Nøklene er
// stabile status-id-er fra `getBankLineStatus`.
const BANK_RECON_STATUS_LABEL = {
  get ny() { return t('rcb.status.new', 'Ikke behandlet'); },
  get forslag() { return t('rcb.status.suggested', 'Forslag venter'); },
  get kontroll() { return t('rcb.status.review', 'Til kontroll'); },
  get matchet() { return t('rcb.status.booked', 'Bokført'); },
  get bokfort() { return t('rcb.status.booked', 'Bokført'); },
  get godkjent() { return t('rcb.status.approved', 'Godkjent'); },
  get privat() { return t('rcb.status.private', 'Privat'); },
  get drift() { return t('rcb.status.operating', 'Drift'); },
  get ignorer() { return t('rcb.status.ignored', 'Ignorert'); },
  get avvik() { return t('rcb.status.manual', 'Lagt til manuelt'); },
};

// Hele bankbildet for én periode.
const buildBankReconciliationView = ({ periodId } = {}) => {
  const range = bankReconMonthRange(periodId);
  if (!range) return { periodId, accounts: [], lines: [], counts: {}, checks: [] };
  const state = mergeState(runtime.appState);
  const reports = getReportsModel({
    year: range.year, months: [range.monthIndex, range.monthIndex], includeChart: false, includeResult: false,
  });
  const trial = new Map();
  for (const group of safeArray(reports?.trial)) {
    for (const account of safeArray(group.accounts)) trial.set(String(account.nr || ''), account);
  }

  const accounts = [];
  const lines = [];
  const duplicateKeys = new Map();

  const sourceAccounts = safeArray(state.settings?.bankAccounts)
    .filter((account) => account.active !== false && account.reconcile !== false && account.kind !== 'private');

  // Duplikatnøklene telles opp på tvers av kontoene før flaggene settes.
  const entriesByAccount = new Map();
  for (const account of sourceAccounts) {
    const rows = bankReconLinesFor(state, account, range);
    entriesByAccount.set(account.id, rows);
    for (const entry of rows) {
      const key = bankReconDuplicateKey({ ...entry, line: { ...entry.line, bankAccountId: account.id } });
      duplicateKeys.set(key, (duplicateKeys.get(key) || 0) + 1);
    }
  }

  for (const account of sourceAccounts) {
    const ledgerCode = getLedgerAccountCode(account, '');
    const rows = entriesByAccount.get(account.id) || [];
    const trialRow = ledgerCode ? trial.get(ledgerCode) : null;
    // Banksaldoen er saldoen på den SISTE linjen i perioden, ikke summen av
    // bevegelsene: en utskrift som starter midt i måneden har ingen inngående
    // saldo å legge til.
    const withBalance = rows.filter((entry) => Number.isFinite(Number(entry.line?.balance)));
    const bankBalance = withBalance.length ? num(withBalance[withBalance.length - 1].line.balance) : null;
    const bookedBalance = trialRow ? num(trialRow.ut) : null;

    for (const entry of rows) {
      const withAccount = { ...entry, line: { ...entry.line, bankAccountId: account.id } };
      const status = getBankLineStatus(entry.line, entry.source);
      lines.push({
        id: entry.line.id,
        statementId: entry.statement.id,
        bankAccountId: account.id,
        ledgerCode,
        accountLabel: getBankAccountLabel(account),
        date: entry.date,
        description: entry.line.description || t('rcb.line.fallback', 'Banklinje'),
        amount: num(entry.line.amount),
        balance: Number.isFinite(Number(entry.line.balance)) ? num(entry.line.balance) : null,
        status,
        statusLabel: BANK_RECON_STATUS_LABEL[status] || status,
        match: bankReconMatchOf(status),
        matchLabel: BANK_RECON_MATCH[bankReconMatchOf(status)].label,
        booked: Boolean(entry.line.matchedEntryId),
        attachmentName: entry.line.attachmentName || '',
        flags: bankReconFlags(withAccount, duplicateKeys),
      });
    }

    // Kontoens egen valuta. Banken rapporterer saldoen i den, hovedboken forer
    // den i kroner — og de to kan ikke sammenliknes direkte. Differansen mellom
    // dem er ikke en feil, den er en omregning, og den krever kursen paa
    // balansedagen.
    const currency = String(account.currency || 'NOK').toUpperCase();
    accounts.push({
      id: account.id,
      currency,
      isForeignCurrency: currency !== 'NOK',
      ledgerCode,
      label: getBankAccountLabel(account),
      accountNumber: account.accountNumber || '',
      source: rows[0]?.source === 'private' ? t('rcb.source.private', 'Privatkonto') : (rows.length ? t('rcb.source.statement', 'Bankutskrift') : t('rcb.source.none', 'Ingen utskrift')),
      bookedBalance,
      bankBalance,
      // Uten et banktall finnes ingen differanse å snakke om — `null` betyr
      // «ikke sammenliknet», ikke «går opp». Samme skille som i avstemmingen.
      // En valutakonto har ingen direkte differanse: bokfort er NOK, bank er
      // valuta. Aa trekke dem fra hverandre ville gitt et tall som ser ut som
      // et avvik og ikke er det.
      difference: bankBalance === null || bookedBalance === null || currency !== 'NOK'
        ? null
        : Math.round((bookedBalance - bankBalance) * 100) / 100,
      // Inngaende saldo er forrige periodes utgaende. Den ligger allerede i
      // saldobalansen — ingen grunn til aa kjore rapporten for forrige maned.
      openingBalance: trialRow ? num(trialRow.inn) : null,
      change: trialRow ? num(trialRow.endring) : null,
      lineCount: rows.length,
      // Hvilke utskrifter grunnlaget faktisk bestaar av. Filnavnet er det
      // eneste vi vet om kilden — formatet er ikke lagret.
      sources: [...new Set(rows.map((entry) => entry.statement?.fileName).filter(Boolean))],
      coverage: bankReconCoverage(rows, range),
    });
  }

  // Hovedbokslinjene paa bankkontoene i samme periode. De svarer paa det
  // bankutskriften ikke kan: hva som er bokfort uten aa ha naadd banken.
  const ledgerCodes = new Set(accounts.map((account) => account.ledgerCode).filter(Boolean));
  const ledgerLines = ledgerCodes.size
    ? buildLedgerLines({ from: range.from, to: range.to, accountCodes: [...ledgerCodes], state })
    : [];
  const transit = [];
  // Bokforingen hver banklinje er koblet til, etter samme regel som avgjor hva
  // som staar igjen uten banktreff.
  const bookedByLine = new Map();
  for (const account of accounts) {
    if (!account.ledgerCode) continue;
    const { open, pairs } = bankReconUnmatchedPostings({
      ledgerLines: ledgerLines.filter((line) => line.accountCode === account.ledgerCode),
      bankLines: lines.filter((line) => line.ledgerCode === account.ledgerCode),
      referenceDate: range.to,
    });
    for (const [lineId, posting] of pairs) bookedByLine.set(lineId, posting);
    for (const entry of open) transit.push({ ...entry, accountLabel: account.label, ledgerCode: account.ledgerCode });
    // Kontoens egen andel av det som er bokfort uten banktreff. Det er dette
    // som forklarer nettopp DENNE kontoens differanse — summen for alle
    // kontoene sier ingenting om den ene som ikke gaar opp.
    account.transitTotal = Math.round(open.reduce((total, entry) => total + num(entry.amount), 0) * 100) / 100;
    account.transitCount = open.length;
  }
  const internalTransfers = bankReconInternalTransfers(ledgerLines, ledgerCodes);
  const feesAndInterest = bankReconFeesAndInterest(ledgerLines, ledgerCodes);

  // Bokforingssiden legges paa linjene etter at matchingen er kjort: for den er
  // kjort vet vi ikke hvilken postering linjen hoerer til.
  for (const line of lines) {
    const posting = bookedByLine.get(line.id);
    line.bookedDate = posting ? posting.date : null;
    line.bookedAmount = posting ? num(posting.amount) : null;
    line.voucherNumber = posting ? posting.voucherNumber || '' : '';
    line.difference = posting ? Math.round((num(line.amount) - num(posting.amount)) * 100) / 100 : null;
  }

  const has = (flag) => lines.filter((line) => line.flags.includes(flag)).length;
  const counts = {
    total: lines.length,
    booked: lines.filter((line) => line.booked).length,
    ikkeBokfort: has('ikke_bokfort'),
    tilKontroll: has('til_kontroll'),
    manglerBilag: has('mangler_bilag'),
    muligDuplikat: has('mulig_duplikat'),
    utenBanktreff: transit.length,
    gamleUtenBanktreff: transit.filter((entry) => entry.stale).length,
  };

  // De automatiske kontrollene, med tallet de fant. De krysser ikke av noe i den
  // faglige sjekklista — de forteller hva den som krysser av bør ha sett på.
  const checks = [
    { key: 'dekning', label: t('rcb.check.coverage', 'Bankgrunnlaget dekker hele perioden'), value: accounts.filter((account) => !account.coverage.complete).length, invert: true },
    { key: 'saldo', label: t('rcb.check.balance', 'Bokført saldo stemmer med banksaldo'), value: accounts.filter((account) => account.difference !== null && Math.abs(account.difference) > 0.005).length, invert: true },
    { key: 'ikke_bokfort', label: t('rcb.check.notBooked', 'Alle banklinjer er bokført'), value: counts.ikkeBokfort, invert: true },
    { key: 'til_kontroll', label: t('rcb.check.review', 'Ingen linjer står til manuell kontroll'), value: counts.tilKontroll, invert: true },
    { key: 'mangler_bilag', label: t('rcb.check.missingVoucher', 'Utbetalinger har bilag'), value: counts.manglerBilag, invert: true },
    { key: 'mulig_duplikat', label: t('rcb.check.duplicate', 'Mulige duplikater er vurdert'), value: counts.muligDuplikat, invert: true },
    { key: 'uten_banktreff', label: t('rcb.check.noBankMatch', 'Alle bokføringer på bankkontoene har en banklinje'), value: counts.utenBanktreff, invert: true },
    { key: 'gammel_transitt', label: t('rcb.check.staleTransit', 'Ingen gamle bokføringer venter på banken'), value: counts.gamleUtenBanktreff, invert: true },
    { key: 'intern', label: t('rcb.check.internal', 'Interne overføringer går opp'), value: internalTransfers.filter((entry) => Math.abs(entry.difference) > 0.005).length, invert: true },
  ];

  return {
    periodId, from: range.from, to: range.to,
    accounts, lines, counts, checks, transit, internalTransfers, feesAndInterest,
    // Uten en hovedbokskonto paa bankkontoen finnes det ingen hovedbokslinjer aa
    // sammenlikne med, og da er transitt, interne overforinger og gebyrer tomme
    // fordi vi ikke saa etter — ikke fordi det ikke var noe. Flata maa kunne si
    // hvilken av delene.
    ledgerLinked: ledgerCodes.size > 0,
    summary: bankReconSummary({ accounts, lines, counts, transit }),
    matchSummary: bankReconMatchSummary({ lines, transit }),
  };
};

Object.assign(window, { buildBankReconciliationView, bankReconMonthRange, BANK_RECON_STATUS_LABEL });
